import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import s from './Dropzone.module.css';
import sg from '../../../../styles/index.module.css';
import { updateInventoryPhoto } from '../../../store/slices/boardSlice';

const getColor = (props) => {
    if (props.isDragAccept) {
        return '#00e676';
    }
    if (props.isDragReject) {
        return '#ff1744';
    }

    return 'transparent';
};

const getPadding = (props) => {
    if (props.isDragActive) {
        return '11px';
    }

    return '11px';
};

const containerStyle = {
    position: 'relative',
    flex: 1,
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    borderWidth: '2px',
    borderRadius: '2px',
    borderStyle: 'dashed',
    boxSizing: 'border-box',
    backgroundColor: '#fafafa',
    color: '#7c7c7c',
    fontFamily: 'Roboto, sans-serif',
    outline: 'none',
    justifyContent: 'center',
};

export function Dropzone({
    inventoryGuid,
    setNewInventoryLog,
    warehouseId,
    inventoryData,
    setInventoryData,
    setImage,
}) {
    const [isRejectedMode, setIsRejectedMode] = useState(false);
    const [rejectedText, setRejectedText] = useState('');
    const [isDisabled, setIsDisabled] = useState(false);
    const dispatch = useDispatch();
    const maxFiles = 10;
    const loadPhotos = (photos) => {
        setIsDisabled(true);
        const formData = new FormData();
        photos.forEach((photo) => {
            formData.append('files[]', photo);
        });

        const url = helpable.replaceUrlParameters(routable.getRoute('uploadInventoryPhotos'), {
            _guid_: inventoryGuid,
        });
        fetch(url, {
            method: 'POST',
            body: formData,
        })
            .then((response) => response.json())
            .then((data) => {
                setIsDisabled(false);
                setNewInventoryLog(data.response);
                setInventoryData({
                    ...inventoryData,
                    // eslint-disable-next-line camelcase
                    preview_url: data.response.photos[data.response.photos.length - 1].original_url,
                });
                setImage(data.response.photos[data.response.photos.length - 1].original_url);
                if (!inventoryData.preview_url) {
                    setInventoryData({
                        ...inventoryData,
                        // eslint-disable-next-line camelcase
                        preview_url: data.response.photos[0].original_url,
                    });
                    dispatch(
                        updateInventoryPhoto({
                            preview: data.response.photos[0].original_url,
                            warehouseId,
                            inventoryGuid,
                        })
                    );
                }
            })
            .catch(() => {
                setIsDisabled(false);
            });
    };

    const setErrorText = (errors) => {
        let text = '';
        errors.forEach((error) => {
            if (error === 'file-invalid-type') {
                text = i18next.t('Dropzone.setErrorText.invalid_format')
                    + ' (jpeg, jpg, png, gif).';
            } else if (error === 'file-too-large') {
                text = i18next.t('Dropzone.setErrorText.size_limit_first')
                    + ' 5 '
                    + i18next.t('Dropzone.setErrorText.size_limit_second');
            } else if (error === 'too-many-files') {
                text = i18next.t('Dropzone.setErrorText.amount_limit_first')
                    + ' 10 '
                    + i18next.t('Dropzone.setErrorText.amount_limit_second');
            }
        });
        setRejectedText(text);
    };

    const onDrop = useCallback(
        (acceptedFiles, rejections) => {
            setIsRejectedMode(false);
            if (rejections.length === 0 && rejections.length <= maxFiles) {
                loadPhotos(acceptedFiles);
            } else {
                setIsRejectedMode(true);
            }
            const errors = [];
            rejections.forEach((item) => {
                if (!errors.includes(item.errors[0].code)) {
                    errors.push(item.errors[0].code);
                }
            });
            setErrorText(errors);
        },
        [loadPhotos]
    );

    const onDragEnter = useCallback(() => {
        setIsRejectedMode(false);
    }, []);

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        onDrop,
        onDragEnter,
        accept: 'image/png, image/jpg, image/jpeg, image/gif',
        maxFiles,
        maxSize: 5000000,
        disabled: isDisabled,
    });

    return (
        <div className={s.dropzoneWrapper}>
            <div className={s.dropzone}>
                <div
                    {...getRootProps({
                        isDragActive,
                        isDragAccept,
                        isDragReject,
                        className: isDisabled ? 'dropzone disabled' : '',
                        style: {
                            ...containerStyle,
                            borderColor: getColor({ isDragAccept, isDragReject }),
                            padding: getPadding({ isDragActive }),
                        },
                    })}
                    data-id="upload-inventory-images-wrapper"
                >
                    <input {...getInputProps()} data-id="upload-inventory-images" />
                    <div className={s.uploadImage}>
                        {!isDragActive && !isDisabled && (
                            <span className={s.uploadImageText}>
                                {i18next.t('Dropzone.return.uploadImageText.isDragActive')}
                            </span>
                        )}
                        {isDragAccept && (
                            <span className={s.uploadImageText}>
                                {i18next.t('Dropzone.return.uploadImageText.isDragAccept')}
                            </span>
                        )}
                        {isDragReject && (
                            <span className={s.uploadImageText}>
                                {i18next.t('Dropzone.return.uploadImageText.isDragReject')}
                            </span>
                        )}
                        {isDisabled && <span>
                                {i18next.t('Dropzone.return.uploadImageText.isDisabled')}
                        </span>}
                    </div>
                </div>
                <div className={s.dropzoneMessage}>
                    {isRejectedMode && rejectedText !== '' && (
                        <span data-id="error-text" className={sg.fadeIn}>
                            {rejectedText}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
